"use client";

import { usePathname, useRouter } from "next/navigation";
import {
  ComponentPropsWithoutRef,
  FC,
  useCallback,
  useContext,
  useEffect,
} from "react";
import { buildLoginRedirectParam } from "../helpers/signupRedirect";
import { useExternalSession } from "../hooks/useExternalSession";
import { useGuest } from "../hooks/useGuest";
import { ModalContext } from "../hooks/useModal";
import { usePostHogIdentify } from "../hooks/usePostHogIdentify";
import Info from "./Info";
import { useSessionContext } from "./SupabaseProvider";

type AuthRedirectProps = {} & ComponentPropsWithoutRef<"div">;

const publicRoutes = [
  "/",
  "/login",
  "/signup",
  "/pricing",
  "/terms-of-services",
  "/privacy-policy",
  "/support",
  "/claude",
  "/clip-download",
  "/affiliate",
  "/embed/*",
  "/alternatives/*",
  "/briefs/*",
  "/tools/*",
  "/blog/*",
  "/test",
  "/404",
  "/master-service-agreement",
  "/data-protection-agreement",
  // "/editor/*",
  "/auth/reset-password",
  "/auth/confirm-recovery",
  "/api",
  // Handles its own sign-in redirect so `?authorization_id=` survives it.
  "/oauth/consent",
];

function extractMessageFromHash(hash: string): string | null {
  // Assuming the hash is provided as an argument or you can use window.location.hash directly
  // Remove the '#' and parse the remaining string as query parameters
  const params = new URLSearchParams(hash.substring(1)); // Remove '#' with substring

  // Extract the 'message' parameter
  let message = params.get("message");

  if (message === null) {
    message = params.get("error_description");
  }

  // If message is not null, decode URI component to handle special characters
  return message ? decodeURIComponent(message) : null;
}

const isPublicRoute = (route: string) => {
  return publicRoutes.some((publicRoute) => {
    if (publicRoute.endsWith("/*")) {
      // Check for wildcard match
      return route.startsWith(publicRoute.slice(0, -2));
    }
    return route === publicRoute;
  });
};

const AuthRedirect: FC<AuthRedirectProps> = ({ ...rest }) => {
  useGuest(); // Handle guest export url
  usePostHogIdentify();

  const { isLoading: sessionIsLoading, session } = useSessionContext();
  const {
    isLoading: externalSessionIsLoading,
    externalSession,
    externalSessionError,
  } = useExternalSession();

  const router = useRouter();
  const pathname = usePathname();
  const modal = useContext(ModalContext);

  const isLoading = sessionIsLoading || externalSessionIsLoading;
  const needRedirect =
    !session && !externalSession && !isPublicRoute(pathname ?? "/");

  const displayErrorInModal = useCallback(
    (error: string) => {
      modal?.open(
        <Info
          description={error}
          onClickCta={() => {
            modal?.close();
            window.location.hash = "";
          }}
          ctaLabel="Got it"
        />,
      );
    },
    [modal],
  );

  useEffect(() => {
    if (isLoading) return;

    // Handle external session error first
    // if (externalSessionError) {
    //   displayErrorInModal(externalSessionError);
    //   return;
    // }

    // Handle hash message
    const message = extractMessageFromHash(window.location.hash);
    if (message) {
      displayErrorInModal(message);
      return;
    }

    // Handle redirect last. `replace`, not `push`: a pushed entry makes Back
    // bounce straight into this redirect again, and inside a customer iframe
    // it would add an entry to the host page's history. The query comes
    // along so a deep link survives sign-in (e.g. `/spaces?v=` from a signup
    // email opened in another browser, which confirms the account but can't
    // finish the PKCE sign-in there).
    if (needRedirect) {
      router.replace(
        `/login?redirect=${buildLoginRedirectParam(
          window.location.pathname,
          window.location.search,
        )}`,
      );
    }
  }, [
    isLoading,
    needRedirect,
    externalSessionError,
    displayErrorInModal,
    pathname,
    router,
  ]);

  return rest.children;
};

export default AuthRedirect;
